import { get } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Table } from "semantic-ui-react";
import { FieldArray } from "formik";

import FormikAPIErrorMessage from "../forms/formik/FormikAPIErrorMessage.jsx";
import SegmentMessage from "../forms/messages/SegmentMessage.jsx";
import SSHRow from "./SSHRow.jsx";
import SSHActions from "./SSHActions.jsx";

import { PANEL_STATES } from "../../state/reducers/projectWizard/projectWizardConstants.js";
import { ROW_STATE } from "../forms/formConstants.js";
import { AT_TYPES } from "../../constants/entity.js";

import accountSelectors from "../../state/reducers/account/accountSelectors.js";

import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class SSHTable extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    limits: PropTypes.object,
    formikProps: PropTypes.object.isRequired, // required for ResourceBlock selectors to function
    showIsUsed: PropTypes.bool.isRequired,
    showApplyExisting: PropTypes.bool.isRequired,
    showNotRequired: PropTypes.bool.isRequired,
    showActionsTop: PropTypes.bool.isRequired,
    apiErrors: PropTypes.object,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    showIsUsed: false,
    showApplyExisting: true,
    showNotRequired: true,
    showActionsTop: false,
    copyKey: "ssh_keys"
  };

  // -- DATA MANAGEMENT -- //

  static newInstance(state) {
    return {
      "@type": AT_TYPES.SSH_KEY,
      name: "",
      value: "",
      state: state || ROW_STATE.NEW
    };
  }

  resetBaseValue = () => {
    const { id, formikProps } = this.props;

    // if the user has previously skipped the panel reinitialise the panel value to an array
    const value = get(formikProps, `values.${id}`, null);
    if (value === PANEL_STATES.SKIPPED) {
      formikProps.setFieldValue(`${id}`, []);
    }
  };

  skipPanel = () => {
    const { id, formikProps } = this.props;

    formikProps.setFieldValue(`${id}`, PANEL_STATES.SKIPPED);

    return null;
  };

  // -- HANDLERS -- //

  addInstance = (state, push) => {
    // if the user has previously skipped the panel reinitialise the panel value to an array
    this.resetBaseValue();

    return push(SSHTable.newInstance(state));
  };

  editInstance = id => {
    this.props.formikProps.setFieldValue(`${id}.state`, ROW_STATE.EDIT);
  };

  deleteInstance = id => {
    this.props.formikProps.setFieldValue(`${id}.state`, ROW_STATE.DELETE);
  };

  resetInstance = id => {
    const { formikProps } = this.props;

    formikProps.setFieldValue(
      `${id}.name`,
      get(formikProps, `initialValues.${id}.name`, "")
    );
    formikProps.setFieldValue(
      `${id}.value`,
      get(formikProps, `initialValues.${id}.value`, "")
    );
    formikProps.setFieldValue(
      `${id}.user`,
      get(formikProps, `initialValues.${id}.user`, "")
    );
    formikProps.setFieldValue(`${id}.state`, ROW_STATE.VIEW);
  };

  // -- RENDER -- //

  renderRows = (instances, remove) => {
    const {
      id,
      formikProps,
      existingSSHKeys,
      showIsUsed,
      disabled,
      apiErrors,
      copyKey
    } = this.props;

    return instances.map((instance, index) => {
      const instanceId = `${id}[${index}]`;

      const mode = get(
        formikProps,
        `values.${instanceId}.state`,
        ROW_STATE.VIEW
      );

      return (
        <SSHRow
          id={instanceId}
          key={instanceId}
          formikProps={formikProps}
          mode={mode}
          options={mode === ROW_STATE.APPLY_EXISTING ? existingSSHKeys : null}
          // onEdit={this.editInstance} // edit may be possible in future
          onDelete={this.deleteInstance}
          onRemove={() => remove(index)}
          onReset={this.resetInstance}
          showIsUsed={showIsUsed}
          readOnly={false}
          disabled={disabled}
          apiErrors={apiErrors}
          copyKey={copyKey}
        />
      );
    });
  };

  renderActions = (skipped, push) => {
    const { showApplyExisting, showNotRequired, showActionsTop } = this.props;

    return (
      <SSHActions
        showApplyExisting={showApplyExisting}
        showNotRequired={showNotRequired}
        showActionsTop={showActionsTop}
        skipped={skipped}
        push={push}
        onSkip={this.skipPanel}
        onAdd={this.addInstance}
      />
    );
  };

  render() {
    const { id, apiErrors, showIsUsed, showActionsTop, copyKey, copy } =
      this.props;

    return (
      <FieldArray
        name={id}
        render={({ push, remove, form: { values } }) => {
          const instances = values[id];
          const skipped = instances === PANEL_STATES.SKIPPED;
          const hasInstances = !skipped && instances && instances.length > 0;

          return (
            <Fragment>
              {showActionsTop && this.renderActions(skipped, push)}

              <FormikAPIErrorMessage
                name={id}
                segment={true}
                apiErrors={apiErrors}
              />

              {skipped && (
                <SegmentMessage
                  info={true}
                  title={copy(`${copyKey}.skipped_message.title`)}
                  message={copy(`${copyKey}.skipped_message.message`)}
                />
              )}

              {hasInstances && (
                <Table celled basic striped className="clear-both">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell className="ssh-name-cell">
                        {copy(`${copyKey}.name_label`)}
                      </Table.HeaderCell>
                      <Table.HeaderCell className="ssh-key-cell">
                        {copy(`${copyKey}.public_key_label`)}
                      </Table.HeaderCell>
                      {showIsUsed === true && (
                        <Table.HeaderCell collapsing>
                          {copy(`${copyKey}.is_used_label`)}
                        </Table.HeaderCell>
                      )}
                      <Table.HeaderCell collapsing />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>{this.renderRows(instances, remove)}</Table.Body>
                </Table>
              )}

              {!showActionsTop && this.renderActions(skipped, push)}
            </Fragment>
          );
        }}
      />
    );
  }
}

const mapStateToProps = state => ({
  existingSSHKeys: accountSelectors.selectSSHKeyOptionData(state),
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(SSHTable);
